/**
 * Mapa de rotas (app.md §6).
 * Fase 1: AppShell + Home + Listas (emails/phones/places) + Leads + Places.
 * Rotas de captura/enhance/IA entram nas fases seguintes.
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { HomePage } from '../pages/HomePage';
import { RequireAuth } from './guards';
import { CaptureRedirect } from './CaptureRedirect';
import { RouteError } from './RouteError';
import { PlacesRedirect } from './PlacesRedirect';
import { pageLoaders } from './prefetch';

/*
 * A Home vem no pacote de entrada (é a tela de chegada). As outras telas são baixadas sob
 * demanda pelo `lazy` das rotas: enquanto a próxima tela baixa, a atual fica na tela e a barra
 * amarela do topo (TopProgress) mostra o andamento.
 */

export const router = createBrowserRouter([
  { path: '/onboarding', errorElement: <RouteError />, lazy: async () => ({ Component: (await pageLoaders.onboarding()).OnboardingPage }) },
  { path: '/activate', errorElement: <RouteError />, lazy: async () => ({ Component: (await pageLoaders.activate()).ActivatePage }) },
  { path: '/uninstall', errorElement: <RouteError />, lazy: async () => ({ Component: (await pageLoaders.uninstall()).UninstallPage }) },
  { path: '/unsubscribe', errorElement: <RouteError />, lazy: async () => ({ Component: (await pageLoaders.unsubscribe()).UnsubscribePage }) },
  // Link do email de autorização do Watchdog: sem login e também no celular (main.tsx).
  { path: '/authorize', errorElement: <RouteError />, lazy: async () => ({ Component: (await pageLoaders.authorize()).AuthorizePage }) },
  {
    path: '/',
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'capture', element: <CaptureRedirect /> },
      {
        path: 'emails',
        lazy: async () => {
          const { ListsPage } = await pageLoaders.lists();
          return { element: <ListsPage module="emails" /> };
        },
      },
      {
        path: 'phones',
        lazy: async () => {
          const { ListsPage } = await pageLoaders.lists();
          return { element: <ListsPage module="phones" /> };
        },
      },
      { path: 'places', lazy: async () => ({ Component: (await pageLoaders.places()).PlacesPage }) },
      { path: 'places/lists', lazy: async () => ({ Component: (await pageLoaders.placesLists()).PlacesListsPage }) },
      { path: 'list/:module/:id/leads', lazy: async () => ({ Component: (await pageLoaders.leads()).LeadsPage }) },
      // Endereço antigo da planilha/mapa de Places: a tela nova é /places.
      { path: 'list/:module/:id/places', element: <PlacesRedirect /> },
      { path: 'help', lazy: async () => ({ Component: (await pageLoaders.help()).HelpPage }) },
      {
        path: 'leadtalk',
        lazy: async () => {
          const { MultimediaPage } = await pageLoaders.multimedia();
          return { element: <MultimediaPage format="leadtalk" /> };
        },
      },
      {
        path: 'leadmusic',
        lazy: async () => {
          const { MultimediaPage } = await pageLoaders.multimedia();
          return { element: <MultimediaPage format="leadmusic" /> };
        },
      },
      // Tutorial em vídeo retirado (29/10/2026): links antigos voltam ao Início.
      { path: 'tutorial', element: <Navigate to="/" replace /> },
      { path: 'referral', lazy: async () => ({ Component: (await pageLoaders.referral()).ReferralPage }) },
      {
        path: 'leadexplorer',
        lazy: async () => ({ Component: (await pageLoaders.leadexplorer()).LeadExplorerPage }),
      },
      // Grupos do WhatsApp (decisões 414 a 423): um endereço por marca, a mesma página.
      { path: 'ferramentas/grupos-whatsapp', lazy: async () => ({ Component: (await pageLoaders.whatsappGroups()).WhatsappGroupsPage }) },
      { path: 'tools/whatsapp-groups', lazy: async () => ({ Component: (await pageLoaders.whatsappGroups()).WhatsappGroupsPage }) },
      { path: 'subscribe', lazy: async () => ({ Component: (await pageLoaders.subscribe()).SubscribePage }) },
      // Conta (decisão 195): uma página por item do menu Conta, sem menu interno.
      { path: 'account', lazy: async () => ({ Component: (await pageLoaders.account()).AccountPage }) },
      { path: 'account/devices', lazy: async () => ({ Component: (await pageLoaders.account()).AccountDevicesPage }) },
      { path: 'account/preferences', lazy: async () => ({ Component: (await pageLoaders.account()).AccountPreferencesPage }) },
      { path: '*', lazy: async () => ({ Component: (await pageLoaders.notFound()).NotFoundPage }) },
    ],
  },
]);
